import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, VStack } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import { copy } from 'toolkit/utils/htmlEntities';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';

const MIDAS_LINKS = [
  { icon: 'globe' as const, iconSize: '18px', text: 'Website', url: 'https://onmidas.xyz' },
  { icon: 'docs' as const, iconSize: '18px', text: 'Developers', url: 'https://onmidas.xyz/developers' },
  { icon: 'gas' as const, iconSize: '18px', text: 'RPC & faucet', url: 'https://onmidas.xyz' },
  { icon: 'rocket' as const, iconSize: '18px', text: 'Technology', url: 'https://onmidas.xyz/technology' },
];

const Footer = () => {

  const renderNetworkInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Flex
        gridArea={ gridArea }
        flexWrap="wrap"
        columnGap={ 8 }
        rowGap={ 6 }
        mb={{ base: 5, lg: 10 }}
        _empty={{ display: 'none' }}
      >
        { !config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus/> }
        <NetworkAddToWallet/>
      </Flex>
    );
  }, []);

  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Box gridArea={ gridArea }>
        <Text textStyle="xs" fontWeight={ 600 }>Midas Chain</Text>
        <Text mt={ 3 } fontSize="xs" maxW="460px">
          The verifiable market layer. A non-custodial L2 for derivatives, outcomes and provably-fair
          markets, trade, settle and prove without trusting the operator.
        </Text>
        <Box mt={ 6 } alignItems="start" textStyle="xs">
          <Text>Testnet { copy } Chain ID 64327 { copy } settles to Ethereum Sepolia.</Text>
          <Text>Copyright { copy } Midas { (new Date()).getFullYear() }</Text>
        </Box>
      </Box>
    );
  }, []);

  const containerProps: HTMLChakraProps<'div'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'border.divider',
  };

  const contentProps: GridProps = {
    px: { base: 4, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 12, '2xl': 6 },
    py: { base: 4, lg: 8 },
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
    maxW: `${ CONTENT_MAX_WIDTH }px`,
    m: '0 auto',
  };

  return (
    <Box { ...containerProps }>
      <Grid
        { ...contentProps }
        gridTemplateAreas={{
          lg: `
          "network links"
          "info links"
        `,
        }}
      >
        { renderNetworkInfo({ lg: 'network' }) }
        { renderProjectInfo({ lg: 'info' }) }

        <Box gridArea={{ lg: 'links' }} justifySelf={{ lg: 'flex-end' }} mt={{ base: 8, lg: 0 }}>
          <Text fontWeight={ 500 } mb={ 3 }>Midas Chain</Text>
          <VStack gap={ 1 } alignItems="start">
            { MIDAS_LINKS.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
          </VStack>
        </Box>
      </Grid>
    </Box>
  );
};

export default React.memo(Footer);
